import { Body, Button, Form, Header, Spacer } from '@spritz-finance/ui'
import { useState } from 'react'
import { SubmitHandler } from 'react-hook-form'
import { Column } from '../../../../components/Layout/Column'
import { Row } from '../../../../components/Layout/Row'
import { useAddCustomBillMutation } from '../../../../graphql/generated/graphql'
import { allBillTypes, merchantBillTypes } from '../../utils/merchantBillTypes'
import { AddAccountForm, AddAccountFormData } from './AddAccountForm'
import { BackButton } from './BackButton'
import { CantPayBillDialog } from './CantPayBillDialog'
import { useLinkBillContext } from './LinkBill'
import { MerchantLogo } from './MerchantSelect'
import { LinkBill } from './types'

export const LinkAccount = () => {
    const { merchant, setStep, onLinkBill, onCantPayBill, selectBillType } = useLinkBillContext()
    const merchantTypes = merchant ? merchantBillTypes(merchant) : allBillTypes
    const selectedType = selectBillType?.type ?? null

    const [showCantPayBill, setShowCantPayBill] = useState<boolean>(false)
    const [addCustomBill, { loading: addCustomBillLoading, error: addCustomBillError }] =
        useAddCustomBillMutation({
            refetchQueries: ['BillsData'],
            awaitRefetchQueries: true,
        })

    if (!merchant) return null

    const addNewBill = () => {
        setShowCantPayBill(false)
        setStep(merchant.account ? LinkBill.SelectBillType : LinkBill.AddNewBill)
    }

    const submit: SubmitHandler<AddAccountFormData> = async (formData) => {
        const result = await addCustomBill({
            variables: {
                accountId: formData.accountNumber,
                merchantId: merchant.id,
                type: formData.billType,
            },
        })
        const canPay = result.data?.addAccount.capabilities.includes('payments:receive') ?? false

        if (canPay) {
            const bill = result.data?.addAccount
            if (bill) onLinkBill(bill.id)
        } else {
            setShowCantPayBill(true)
        }
    }

    const accountName = merchant.account ?? 'account'

    return (
        <>
            <Column align="center" style={{ position: 'relative' }}>
                <BackButton onClick={addNewBill} />
                <MerchantLogo merchant={merchant} />
                <Spacer height={8} />
                <Body.SmBold>{merchant.name}</Body.SmBold>
                <Spacer height={8} />
                <Row align="left" style={{ width: '100%' }}>
                    <Header.SmBold
                        style={{ fontSize: 18 }}
                    >{`Add your ${accountName} from ${merchant.parentName}`}</Header.SmBold>
                </Row>
                <Spacer height={16} />
                <Form<AddAccountFormData>
                    onSubmit={submit}
                    width="100%"
                    initialValues={{
                        accountNumber: '',
                        billType: selectedType ?? merchantTypes?.[0] ?? null,
                    }}
                >
                    <AddAccountForm
                        submitting={addCustomBillLoading}
                        error={addCustomBillError}
                        billTypeOptions={merchantTypes}
                        preselectedType={!!selectedType}
                    />
                </Form>
            </Column>
            <CantPayBillDialog open={showCantPayBill} onClose={addNewBill}>
                <Row>
                    <Button.CTA responsive variant="secondary" onClick={onCantPayBill}>
                        Close
                    </Button.CTA>
                    <Spacer width={16} />
                    <Button.CTA responsive onClick={addNewBill}>
                        Add another bill
                    </Button.CTA>
                </Row>
            </CantPayBillDialog>
        </>
    )
}
